/**
 * Infinite Scroll Hook
 * Automatically loads more data as user scrolls
 */

import { useEffect, useRef, useCallback } from "react";

export interface UseInfiniteScrollOptions {
  /**
   * Callback to load more data
   */
  onLoadMore: () => void;

  /**
   * Whether there is more data to load
   */
  hasMore: boolean;

  /**
   * Whether data is currently loading
   */
  isLoading: boolean;

  /**
   * Distance from bottom to trigger load (in pixels)
   * @default 300
   */
  threshold?: number;

  /**
   * Root element for intersection observer.
   *
   * M6(c) (revisão final, PR 5) — sem `root` explícito, o observer usa o
   * ancestral `<main>` mais próximo do sentinela, NÃO o viewport: o portal
   * põe `overflow: hidden` em `html`/`body` (globals.css) e quem realmente
   * rola é o `<main>` do `PortalLayout`. Com `root: null` (viewport), o
   * sentinela nunca fica fora da área visível daquele `main` — o
   * `rootMargin` de pré-carregamento nunca dispara antes do fim de verdade.
   * Passe `null` explicitamente se REALMENTE quiser o viewport.
   * @default detecta o `<main>` mais próximo do sentinela
   */
  root?: HTMLElement | null;
}

/**
 * Hook for implementing infinite scroll
 * Uses Intersection Observer API for better performance
 *
 * @example
 * const { loaderRef } = useInfiniteScroll({
 *   onLoadMore: fetchNextPage,
 *   hasMore: hasNextPage,
 *   isLoading: isFetchingNextPage,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} {...item} />)}
 *     <div ref={loaderRef} />
 *   </div>
 * );
 */
export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 300,
  root,
}: UseInfiniteScrollOptions) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;

      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  useEffect(() => {
    const currentLoader = loaderRef.current;
    // M6(c) — `root === undefined` (o caller não passou nada): detecta o
    // `<main>` mais próximo do sentinela. `root === null` explícito
    // continua valendo viewport de verdade.
    const raizResolvida = root !== undefined ? root : (currentLoader?.closest("main") ?? null);

    const options: IntersectionObserverInit = {
      root: raizResolvida,
      rootMargin: `${threshold}px`,
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver(handleObserver, options);

    if (currentLoader) {
      observerRef.current.observe(currentLoader);
    }

    return () => {
      if (observerRef.current && currentLoader) {
        observerRef.current.unobserve(currentLoader);
      }
    };
  }, [handleObserver, root, threshold]);

  return { loaderRef };
};

/**
 * Alternative scroll-based implementation
 * Uses scroll events (less performant than Intersection Observer)
 */
export const useScrollBasedInfinite = ({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 300,
}: Omit<UseInfiniteScrollOptions, "root">) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || isLoading || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom < threshold) {
        onLoadMore();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [hasMore, isLoading, onLoadMore, threshold]);

  return { containerRef };
};
